import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { API_BASE, setAuthToken } from '../api/client';

interface AuthContextType {
    isAuthenticated: boolean;
    username: string | null;
    loading: boolean;
    login: (token: string) => Promise<boolean>;
    logout: () => void;
    token: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

const AUTH_KEY = 'agentk_auth';
// Dev auth bypass must be explicitly enabled; default is off to keep token login testable.
const DEV_BYPASS_AUTH =
    import.meta.env.DEV &&
    /^(1|true|yes|on)$/i.test(import.meta.env.VITE_BYPASS_AUTH ?? '');

function isLocalhost(): boolean {
    const h = window.location.hostname;
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
}
export function AuthProvider({ children }: { children: ReactNode }) {
    const [token, setToken] = useState<string | null>(localStorage.getItem(AUTH_KEY));
    const [username, setUsername] = useState<string | null>(DEV_BYPASS_AUTH ? 'dev-bypass' : null);
    const [loading, setLoading] = useState(true); // start true until probe finishes
    const [authDisabled, setAuthDisabled] = useState(DEV_BYPASS_AUTH);

    // On mount, check if auth is required
    useEffect(() => {
        if (DEV_BYPASS_AUTH || token) {
            setLoading(false);
            return;
        }
        // localhost → skip auth immediately, no backend probe needed
        if (isLocalhost()) {
            setAuthDisabled(true);
            setUsername('localhost');
            setLoading(false);
            return;
        }
        // Remote access → probe backend to check if token is configured
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/api/v1/auth/verify`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({}),
                });
                if (!cancelled && res.ok) {
                    const json = await res.json();
                    const mode = json?.data?.status === 'ok' ? json.data.mode : null;
                    if (mode === 'local' || mode === 'disabled') {
                        setAuthDisabled(true);
                        setUsername('no-auth');
                    }
                }
            } catch {
                // backend unreachable, require login
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const login = async (tokenInput: string): Promise<boolean> => {
        if (DEV_BYPASS_AUTH) {
            return true;
        }

        setLoading(true);
        const rawToken = tokenInput.trim();
        const bearerToken = rawToken.startsWith('Bearer ')
            ? rawToken
            : `Bearer ${rawToken}`;

        try {
            const res = await fetch(`${API_BASE}/api/v1/auth/verify`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': bearerToken
                },
                body: JSON.stringify({ token: rawToken }),
            });

            if (res.ok) {
                setAuthToken(bearerToken);
                setToken(bearerToken);
                setUsername('api-token');
                return true;
            } else {
                return false;
            }
        } catch (e) {
            console.error(e);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        setAuthToken(null);
        setToken(null);
        setUsername(authDisabled ? username : null);
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated: authDisabled || !!token, username, loading, login, logout, token }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
